// src/components/product/ProductSettingsSection.tsx

import { useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { ProductStatus, RequiredDataType } from '@models/product';
import { ProductSaleType, ProductType } from '@apis/product';
import {
  PRODUCT_SALE_TYPE_LABELS,
  PRODUCT_STATUS_LABELS,
  PRODUCT_TYPE_LABELS,
} from '@utils/productLabels';

type StockOption = {
  value: number;
  label: string;
};

interface ProductSettingsSectionProps {
  productStatus: ProductStatus;
  productSaleType: ProductSaleType;
  productType: ProductType;
  requiredFields: RequiredDataType[];
  stockId: number | null;
  stockOptions: StockOption[];
  apiParameter: string | null;
  isApiInputNeeded: boolean;
  onChangeStatus: (value: ProductStatus) => void;
  onChangeSaleType: (value: ProductSaleType) => void;
  onChangeProductType: (value: ProductType) => void;
  onToggleRequiredField: (field: RequiredDataType, checked: boolean) => void;
  onChangeStockId: (value: number | null) => void;
  onChangeApiParameter: (value: string | null) => void;
}

/**
 * Returns the allowed ProductType list for a given Order Type (sale type).
 *
 * REAL_TIME    → ESIM, PSIM_TOPUP
 * RESERVATION  → RESERVE_ESIM, ROUTER
 */
const getAllowedProductTypes = (saleType: ProductSaleType): ProductType[] => {
  if (saleType === ProductSaleType.REAL_TIME) {
    return [ProductType.ESIM, ProductType.PSIM_TOPUP];
  }
  if (saleType === ProductSaleType.RESERVATION) {
    return [ProductType.RESERVE_ESIM, ProductType.ROUTER];
  }
  return Object.values(ProductType);
};

/**
 * ProductSettingsSection
 *
 * High-level configuration panel for a product:
 * 1) Order Type / Product Type / Status
 * 2) Required customer fields
 * 3) Stock linkage and optional API parameter (JSON)
 *
 * Important UX / state guard:
 * - When Order Type changes, the allowed Product Type options change immediately.
 * - MUI Select warns if its current `value` does not exist in the new option list.
 * - To avoid transient "out-of-range value" warnings, we bind the Select to a
 *   "safe" value that is always present in the current option list, while still
 *   normalizing the upstream state via an effect.
 */
const ProductSettingsSection = ({
  productStatus,
  productSaleType,
  productType,
  requiredFields,
  stockId,
  stockOptions,
  apiParameter,
  isApiInputNeeded,
  onChangeStatus,
  onChangeSaleType,
  onChangeProductType,
  onToggleRequiredField,
  onChangeStockId,
  onChangeApiParameter,
}: ProductSettingsSectionProps) => {
  const allowedProductTypes = useMemo(
    () => getAllowedProductTypes(productSaleType),
    [productSaleType],
  );

  /**
   * "Safe" value for the Product Type Select.
   * This prevents MUI out-of-range warnings during the render where the option list changes.
   */
  const safeProductType = useMemo<ProductType>(() => {
    return allowedProductTypes.includes(productType)
      ? productType
      : allowedProductTypes[0];
  }, [allowedProductTypes, productType]);

  /**
   * Normalize the actual productType state after Order Type changes.
   * This keeps the persisted state consistent with the allowed combination rules.
   */
  useEffect(() => {
    if (productType !== safeProductType) {
      onChangeProductType(safeProductType);
    }
  }, [productType, safeProductType, onChangeProductType]);

  const isRequired = (field: RequiredDataType) => requiredFields.includes(field);

  return (
    <Card
      elevation={0}
      sx={(theme) => ({
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 3,
      })}
    >
      <CardHeader
        title={
          <Typography variant="h6" fontWeight={700}>
            Product settings
          </Typography>
        }
        subheader={
          <Typography variant="body2" color="text.secondary">
            Configure product type, required customer fields, and stock / API linkage.
          </Typography>
        }
      />

      <CardContent>
        <Stack spacing={3}>
          {/* 1) Product type & status */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              1. Product type &amp; status
            </Typography>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'flex-start' }}
            >
              {/* Order Type (Sale Type) */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="product-sale-type-label">Order Type</InputLabel>
                <Select
                  labelId="product-sale-type-label"
                  label="Order Type"
                  value={productSaleType}
                  onChange={(e) => onChangeSaleType(e.target.value as ProductSaleType)}
                >
                  {Object.values(ProductSaleType).map((t) => (
                    <MenuItem key={t} value={t}>
                      {PRODUCT_SALE_TYPE_LABELS[t]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Product Type (options depend on Order Type) */}
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="product-type-label">Product Type</InputLabel>
                <Select
                  labelId="product-type-label"
                  label="Product Type"
                  value={safeProductType}
                  onChange={(e) => onChangeProductType(e.target.value as ProductType)}
                >
                  {allowedProductTypes.map((type) => (
                    <MenuItem key={type} value={type}>
                      {PRODUCT_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Product Status */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel id="product-status-label">Product Status</InputLabel>
                <Select
                  labelId="product-status-label"
                  label="Product Status"
                  value={productStatus}
                  onChange={(e) => onChangeStatus(e.target.value as ProductStatus)}
                >
                  {Object.values(ProductStatus).map((status) => (
                    <MenuItem key={status} value={status}>
                      {PRODUCT_STATUS_LABELS[status]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
          </Box>

          <Divider />

          {/* 2) Required customer fields */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              2. Required customer fields
            </Typography>

            <FormGroup row sx={{ gap: 2, '& .MuiFormControlLabel-root': { mr: 3 } }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRequired(RequiredDataType.ACTIVATION_DATE)}
                    onChange={(e) =>
                      onToggleRequiredField(RequiredDataType.ACTIVATION_DATE, e.target.checked)
                    }
                  />
                }
                label="Activation date"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRequired(RequiredDataType.IMEI)}
                    onChange={(e) =>
                      onToggleRequiredField(RequiredDataType.IMEI, e.target.checked)
                    }
                  />
                }
                label="IMEI"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRequired(RequiredDataType.EID)}
                    onChange={(e) => onToggleRequiredField(RequiredDataType.EID, e.target.checked)}
                  />
                }
                label="EID"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRequired(RequiredDataType.CUSTOMER_NAME)}
                    onChange={(e) =>
                      onToggleRequiredField(RequiredDataType.CUSTOMER_NAME, e.target.checked)
                    }
                  />
                }
                label="User name"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRequired(RequiredDataType.PASSPORT_DATA)}
                    onChange={(e) =>
                      onToggleRequiredField(RequiredDataType.PASSPORT_DATA, e.target.checked)
                    }
                  />
                }
                label="Passport"
              />
            </FormGroup>
          </Box>

          <Divider />

          {/* 3) Stock settings */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              3. Stock settings
            </Typography>

            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="stock-code-label">Stock Code</InputLabel>
              <Select
                labelId="stock-code-label"
                label="Stock Code"
                value={stockId ?? ''}
                onChange={(e) => {
                  const value = e.target.value;
                  onChangeStockId(value ? Number(value) : null);
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>

                {stockOptions.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {isApiInputNeeded ? (
              <Box mt={2}>
                <Chip
                  label="API-linked stock"
                  size="small"
                  color="primary"
                  variant="outlined"
                  sx={{ mb: 2 }}
                />

                <TextField
                  label="API parameter (JSON)"
                  placeholder='{"key": "value"}'
                  value={apiParameter ?? ''}
                  onChange={(e) => onChangeApiParameter(e.target.value || null)}
                  size="small"
                  multiline
                  minRows={4}
                  fullWidth
                />

                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 0.5, display: 'block' }}
                >
                  Enter JSON payload used when calling the supplier API for this stock.
                </Typography>
              </Box>
            ) : null}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default ProductSettingsSection;
